import qs from "qs";
import {
	EventStatus,
	ObjectUtils
} from "@prestosports/prestostats-core/Common";
import ServiceRegistry from "./service-registry";
import AuthenticationService from "./authentication-service";
import DateFormat from "../util/date-format";
import apiEndpoints from "./api-endpoints";

const apiInstance = ServiceRegistry.createService(
	process.env.REACT_APP_GAMEDAY_API_ENDPOINT,
	AuthenticationService
);

class GamedayApiClient {
	constructor() {
		this.apiEndpoints = apiEndpoints;
	}

	getEventByExternalId = async (eventId) => {
		const { data } = await apiInstance.get(this.apiEndpoints.EVENT(eventId));
		return data.data;
	};

	async getPlayers(teamId) {
		const { data } = await apiInstance.get(
			this.apiEndpoints.TEAM_PLAYERS(teamId)
		);

		return data.data;
	}

	async getEventPlayers(teamId, eventId) {
		const { data } = await apiInstance.get(
			this.apiEndpoints.EVENT_TEAM_PLAYERS(teamId, eventId)
		);

		return data.data;
	}

	async getTeam(teamId) {
		const { data } = await apiInstance.get(this.apiEndpoints.TEAM(teamId));
		return data.data;
	}

	async getSeasonTeams(seasonId, teamIds) {
		const { data } = await apiInstance.get(
			this.apiEndpoints.SEASON_TEAMS(seasonId),
			{
				params: {
					teamIds,
					includeApiAccess: true
				},
				paramsSerializer: (params) => {
					return qs.stringify(params, { arrayFormat: "repeat" });
				}
			}
		);
		return data.data;
	}

	async getUserTeams(seasonId) {
		const { data } = await apiInstance.get(
			this.apiEndpoints.USER_TEAMS(seasonId),
			{
				params: {}
			}
		);
		return data.data;
	}

	async getLastWeekEvents(sportCodes = []) {
		const now = new Date();
		const startDate = DateFormat.subtractDays(now, 7);
		const { data } = await apiInstance.get(this.apiEndpoints.USER_EVENTS(), {
			params: {
				startDate: DateFormat.toShortISODate(startDate),
				endDate: DateFormat.toShortISODate(now),
				sportId: sportCodes,
				eventStatus: "FINAL"
			},
			paramsSerializer: (params) => {
				return qs.stringify(params, { arrayFormat: "repeat" });
			}
		});
		return data.data;
	}

	async getNextWeekEvents(sportCodes = []) {
		const now = new Date();
		const endDate = DateFormat.addDays(now, 7);
		const status = ["IN_PROGRESS", "NOT_STARTED"];
		const { data } = await apiInstance.get(this.apiEndpoints.USER_EVENTS(), {
			params: {
				startDate: DateFormat.toShortISODate(now),
				endDate: DateFormat.toShortISODate(endDate),
				sportId: sportCodes,
				eventStatus: status
			},
			paramsSerializer: (params) => {
				return qs.stringify(params, { arrayFormat: "repeat" });
			}
		});
		return data.data;
	}

	async getEvents() {
		const { data } = await apiInstance.get(this.apiEndpoints.USER_EVENTS());

		return data.data;
	}

	getSeason = async (seasonId) => {
		const { data } = await apiInstance.get(this.apiEndpoints.SEASON(seasonId));
		return data.data;
	};

	getSeasons = async (sportCodes) => {
		const { data } = await apiInstance.get(this.apiEndpoints.USER_SEASONS(), {
			params: {
				sportId: sportCodes,
				pageSize: 100
			},
			paramsSerializer: (params) => {
				return qs.stringify(params, { arrayFormat: "repeat" });
			}
		});
		return data.data;
	};

	async getEventsByCriteria(
		seasonId,
		{ startDate, endDate, statusCode, teamIds },
		{ pageNumber, pageSize }
	) {
		const params = {
			seasonId,
			eventStatus: statusCode,
			startDate: DateFormat.toShortISODate(startDate),
			endDate: DateFormat.toShortISODate(endDate),
			teamIds: teamIds || [],
			pageNumber,
			pageSize
		};

		const { data } = await apiInstance.get(this.apiEndpoints.USER_EVENTS(), {
			params,
			paramsSerializer: (p) => {
				return qs.stringify(p, { arrayFormat: "repeat" });
			}
		});

		return data;
	}

	async savePlayer(player, remoteTeamId) {
		const { data } = await apiInstance.post(
			this.apiEndpoints.TEAM_PLAYER_SAVE(remoteTeamId),
			player
		);

		return data.data;
	}

	async updatePlayer(player, remoteTeamId) {
		const { data } = await apiInstance.put(
			this.apiEndpoints.TEAM_PLAYER_UPDATE(remoteTeamId, player.playerId),
			player
		);

		return data.data;
	}

	async saveOrUpdatePlayer(player, remoteTeamId) {
		if (player.playerId) {
			return this.updatePlayer(player, remoteTeamId);
		}

		return this.savePlayer(player, remoteTeamId);
	}

	async updateEvent(event) {
		const { teams, score } = event;
		const { awayTeam, homeTeam } = teams;
		const { away, home } = score;

		const isEventStarted = EventStatus.isStarted(event);

		const { data } = await apiInstance.patch(
			this.apiEndpoints.EVENT(event.eventId),
			{
				...event,
				visitorTeamId: awayTeam.teamId,
				homeTeamId: homeTeam.teamId,
				visitorTeamResult: isEventStarted ? away : null,
				homeTeamResult: isEventStarted ? home : null
			}
		);

		return data.data;
	}

	async getTimeZones() {
		const { data } = await apiInstance.get(this.apiEndpoints.TIMEZONES());

		return data.data;
	}

	async getEventsByDateSportCodeAndStatusCode(date, sportCode, statusCodes) {
		const { data } = await apiInstance.get(this.apiEndpoints.USER_EVENTS(), {
			params: {
				startDate: DateFormat.toShortISODate(date),
				endDate: DateFormat.toShortISODate(date),
				sportId: sportCode,
				eventStatus: statusCodes,
				seasonEndingAfter: DateFormat.toShortISODate(new Date())
			},
			paramsSerializer: (params) => {
				return qs.stringify(params, { arrayFormat: "repeat" });
			}
		});

		const sorted =
			data?.data?.sort((a, b) =>
				ObjectUtils.isoDateComparer(a.startDateTime, b.startDateTime)
			) || [];

		return sorted;
	}

	async saveOrUpdatePlayers(teamId, players) {
		const { data } = await apiInstance.post(
			this.apiEndpoints.TEAM_PLAYERS(teamId),
			{ players }
		);

		return data.data;
	}

	async saveEventData(remoteEventId, eventData) {
		return apiInstance.post(
			this.apiEndpoints.EVENT_DATA(remoteEventId, eventData),
			eventData
		);
	}

	async fetchEventData(remoteEventId) {
		const { data } = await apiInstance.get(
			this.apiEndpoints.EVENT_DATA(remoteEventId)
		);
		return data.data;
	}

	async saveBoxscore(boxscore) {
		const { id } = boxscore?.eventInfo || {};
		return apiInstance.post(this.apiEndpoints.SAVE_BOXSCORE(id), boxscore, {
			params: { platform_id: "stats2.0" }
		});
	}

	async saveSeasonSettings(seasonId, settings) {
		return apiInstance.put(
			this.apiEndpoints.SAVE_SEASON_SETTINGS(seasonId),
			settings
		);
	}

	async getSeasonSettings(seasonId) {
		try {
			const S3_BASE_URL = `https://${process.env.REACT_APP_S3_BUCKET}.s3.${process.env.REACT_APP_S3_REGION}.amazonaws.com`;
			const env = process.env.REACT_APP_ENV;

			const s3Instance = ServiceRegistry.createService(S3_BASE_URL);

			const { data, status } = await s3Instance.get(
				this.apiEndpoints.GET_SEASON_SETTINGS(env, seasonId),
				{ headers: { "Cache-Control": "no-store", Pragma: "no-cache" } }
			);

			return status === 200 ? data : null;
		} catch {
			return null;
		}
	}
}

export default new GamedayApiClient();
